/**
 * Account Context - Manages paper/live account state
 *
 * Provides global state for current account type (paper or live)
 * Persists to localStorage and provides warnings for live trading
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type AccountType = 'paper' | 'live';

interface AccountContextType {
  accountType: AccountType;
  setAccountType: (type: AccountType) => void;
  ibkrPort: number;
  isLiveTrading: boolean;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

const STORAGE_KEY = 'trading_account_type';

// Port mappings
const PORT_MAP = {
  paper: 7497,
  live: 7496,
};

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize from localStorage or default to paper
  const [accountType, setAccountTypeState] = useState<AccountType>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored === 'live' || stored === 'paper') ? stored : 'paper';
  });

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, accountType);
  }, [accountType]);

  const setAccountType = (type: AccountType) => {
    // Show confirmation dialog for live trading
    if (type === 'live') {
      const confirmed = window.confirm(
        '⚠️ WARNING: You are switching to LIVE TRADING!\n\n' +
        'Real money will be at risk. All trades will execute with real funds.\n\n' +
        'Are you sure you want to continue?'
      );

      if (!confirmed) {
        return; // User cancelled
      }
    }

    setAccountTypeState(type);
  };

  const value = {
    accountType,
    setAccountType,
    ibkrPort: PORT_MAP[accountType],
    isLiveTrading: accountType === 'live',
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
